<script>
	import { PRConnect } from "@projectroom/ui-lib";
	import axios from "axios";
	import {
		showLoadingPopup,
		hideLoadingPopup,
	} from "./../store/loadingPopupStore.js";
	import { Dialog, Button } from "../../lib/ui";
	import { autoGrow } from "./autoGrow";
	import ImagePicker from "./ImagePicker.svelte";
	import { chatAssetFolder } from "./imageAssetApi.js";

	export let aiImageLists;
	export let chat;
	export let isShow;
	export let imageType;
	export let imageRefSources;

	// ── 선택지 위저드 정의 ──────────────────────────────────────────────────────
	// "키워드로 생성"은 추상적이라 결과가 산으로 감. 대신 저작자가 선택지로 좁혀가게 한다.
	// label = 화면 표시, frag = 최종 프롬프트에 들어갈 구체 문구(서버 중립 가이드 위에 얹힘).
	// 마지막 단계(분위기+설명)는 모든 타입 공통. 참조 이미지가 있으면 종류·화풍 단계는
	// 참조가 대신하므로 건너뛰고 이 마지막 단계만 보여준다.
	const WIZARD = {
		profile: [
			{ key: "subject", title: "무엇을 만들까요?", options: [
				{ label: "사람", frag: "한 명의 사람" },
				{ label: "동물", frag: "동물" },
				{ label: "가상 캐릭터", frag: "가상의 캐릭터" },
				{ label: "사물·로고", frag: "사물 또는 심볼" },
			] },
			{ key: "style", title: "어떤 화풍으로?", options: [
				{ label: "실사 사진", frag: "사실적인 사진 화풍" },
				{ label: "애니·웹툰", frag: "애니메이션·웹툰 일러스트 화풍" },
				{ label: "3D", frag: "3D 렌더 화풍" },
				{ label: "일러스트", frag: "손그림 일러스트 화풍" },
				{ label: "미니멀", frag: "미니멀한 플랫 벡터 화풍" },
			] },
		],
		background: [
			{ key: "place", title: "어떤 장소인가요?", options: [
				{ label: "실내", frag: "실내 공간" },
				{ label: "실외·자연", frag: "야외 자연 풍경" },
				{ label: "도시", frag: "도시 풍경" },
				{ label: "추상·패턴", frag: "추상적인 배경 패턴" },
			] },
			{ key: "style", title: "어떤 화풍으로?", options: [
				{ label: "실사 사진", frag: "사실적인 사진 화풍" },
				{ label: "애니·웹툰", frag: "애니메이션·웹툰 배경 화풍" },
				{ label: "일러스트", frag: "일러스트 화풍" },
				{ label: "미니멀", frag: "미니멀한 플랫 화풍" },
			] },
		],
		scene: [
			{ key: "style", title: "어떤 화풍으로?", options: [
				{ label: "실사 사진", frag: "사실적인 사진 화풍" },
				{ label: "애니·웹툰", frag: "애니메이션·웹툰 화풍" },
				{ label: "일러스트", frag: "일러스트 화풍" },
				{ label: "미니멀", frag: "미니멀한 플랫 화풍" },
			] },
		],
	};
	const MOODS = [
		{ label: "밝은", frag: "밝고 화사한 분위기" },
		{ label: "차분한", frag: "차분하고 잔잔한 분위기" },
		{ label: "진지한", frag: "진지한 분위기" },
		{ label: "귀여운", frag: "귀엽고 사랑스러운 분위기" },
		{ label: "고급", frag: "고급스럽고 세련된 분위기" },
	];

	let addRefenceImageRef;
	// 참조 이미지 위치. 생성 시 서버로 넘겨 실제 시각 참조(image-to-image)로 쓴다.
	// 로컬에서 올리면 **S3 키**, 라이브러리에서 고르면 **전체 URL** 이 담긴다(서버가 둘 다 받는다).
	let refImageKey = "";
	let refPickerOpen = false;
	let isAIImageChecked;

	// 위저드 상태
	let stepIndex = 0;
	let selections = {}; // stepKey -> 선택된 option
	let moodSel = null;
	let description = "";

	$: imagePath = imageType + "-images";
	$: referenceContext = imageRefSources[imageType] || {};
	$: src = referenceContext.src ?? "";
	$: hasRef = !!(src && src.length);
	$: aiImageList = aiImageLists[imageType] ?? [];
	$: hasResults = aiImageList.length > 0;

	$: typeLabel =
		imageType === "profile" ? "프로필" : imageType === "background" ? "배경" : "상황";

	// 참조가 있으면 종류·화풍 단계는 생략(참조가 대신) → 마지막 단계만.
	$: narrowSteps = WIZARD[imageType] || [];
	$: steps = hasRef
		? [{ type: "final" }]
		: [...narrowSteps.map((s) => ({ type: "choice", step: s })), { type: "final" }];
	$: current = steps[Math.min(stepIndex, steps.length - 1)];
	$: isFinal = current && current.type === "final";

	// 팝업이 열릴 때 위저드 초기화(참조 이미지 자체는 공유 소스라 유지).
	let _wasOpen = false;
	$: {
		if (isShow && !_wasOpen) resetWizard();
		_wasOpen = isShow;
	}
	// 참조 이미지 유무가 바뀌면 흐름이 달라지므로 첫 단계로.
	let _prevHasRef = false;
	$: {
		if (hasRef !== _prevHasRef) {
			stepIndex = 0;
			_prevHasRef = hasRef;
		}
	}

	const resetWizard = () => {
		stepIndex = 0;
		selections = {};
		moodSel = null;
		description = "";
	};

	const handleClose = () => {
		chat.assignAIImageList();
	};

	const onRefenceImageFileUpload = (e) => {
		let image = e.target.files[0];
		const key = `chats/${chat.chatId}/common/${imagePath}/${image.name}`;
		refImageKey = key;
		const type = image.type;
		showLoadingPopup("imageUpload");
		const reader = new FileReader();
		reader.onload = (event) => {
			if (!imageRefSources[imageType]) imageRefSources[imageType] = { src: "" };
			imageRefSources[imageType].src = event.target.result;
			imageRefSources = { ...imageRefSources }; // referenceContext 재계산 트리거
		};
		PRConnect.send("/api/File/uploadPresignedUrl", { key, type }, (data) => {
			axios
				.put(data.result, image, { headers: { "Content-Type": type } })
				.then(() => {
					hideLoadingPopup();
					reader.readAsDataURL(image);
				});
		});
	};

	const clickDeleteRefenceImage = () => {
		if (imageRefSources[imageType]) imageRefSources[imageType].src = "";
		imageRefSources = { ...imageRefSources };
		refImageKey = "";
	};

	// 이미 라이브러리에 있는 이미지를 참조로 고른다.
	// ⚠️ 여기서 담는 값은 S3 키가 아니라 **자산의 전체 URL** 이다. 서버 gptImageGenerate 가
	//    키·URL 을 모두 받게 돼 있어 그대로 넘기면 된다(접두어를 잘라 키로 만들면 저장 위치가
	//    바뀌는 순간 깨진다).
	const onPickRefFromLibrary = (e) => {
		const { url } = e.detail;
		refPickerOpen = false;
		if (!url) return;
		refImageKey = url;
		if (!imageRefSources[imageType]) imageRefSources[imageType] = { src: "" };
		imageRefSources[imageType].src = url;
		imageRefSources = { ...imageRefSources }; // referenceContext 재계산 트리거
	};

	// 선택지 → 구체 프롬프트로 조립. 참조가 있으면 selections는 비어 있고 참조가 종류·화풍을 대신한다.
	const buildPrompt = () => {
		const frags = [];
		for (const s of narrowSteps) {
			if (selections[s.key]) frags.push(selections[s.key].frag);
		}
		if (moodSel) frags.push(moodSel.frag);
		const desc = (description || "").trim();
		return [frags.join(", "), desc].filter(Boolean).join(". ");
	};

	const pickChoice = (stepKey, option) => {
		selections[stepKey] = option;
		selections = selections;
		if (stepIndex < steps.length - 1) stepIndex += 1; // 다음 단계로 좁혀간다
	};
	const pickMood = (m) => {
		moodSel = moodSel === m ? null : m;
	};
	const goBack = () => {
		if (stepIndex > 0) stepIndex -= 1;
	};

	const clickAIImageCreate = () => {
		const prompt = buildPrompt();
		const targetPath = imageType == "background" ? "images" : imagePath;
		showLoadingPopup("imageCreate");
		PRConnect.send(
			"/api/Chat/gptImageGenerate",
			{ chatId: chat.chatId, prompt, imageType, targetPath, referenceImage: refImageKey || undefined },
			(data) => {
				hideLoadingPopup();
				if (data.header.status === "success") {
					aiImageLists[imageType] = [data.result, ...aiImageList];
				}
			},
		);
	};

	const clickGeneratedImage = (aiImage) => {
		aiImageList.forEach((e) => (e.check = false));
		aiImage.check = true;
		isAIImageChecked = true;
		aiImageList = [...aiImageList];
	};

	const handleSelectAIImage = () => {
		handleClose();
		let aiImage = aiImageList.find((e) => e.check === true);
		const fileName = aiImage.name;
		if (imageType === "scene") {
			// 씬 라이브러리에 바인딩(렌더용) + 이미지 에셋으로도 등록 → ImagePicker 라이브러리에서 재사용 가능.
			chat.addSceneImage(fileName);
			PRConnect.send("/api/Chat/commitAiImageAsset", { chatId: chat.chatId, imageType, fileName }, () => {});
		} else if (imageType === "profile" || imageType === "background") {
			// AI 이미지를 에셋으로 등록 → 프로필/배경 에셋으로 지정(이미지 자산 통합 관리 대상이 됨).
			PRConnect.send(
				"/api/Chat/commitAiImageAsset",
				{ chatId: chat.chatId, imageType, fileName },
				(data) => {
					if (data.header.status === "success") {
						const { assetId, url } = data.result;
						if (imageType === "profile") chat.changeProfileAsset(assetId, url);
						else chat.changeBackAsset(assetId, url);
					}
				},
			);
		}
	};

	const chipBase =
		"tw-cursor-pointer tw-rounded-full tw-border tw-transition-colors";
</script>

<Dialog open={isShow} title={`AI로 ${typeLabel} 이미지 생성`} on:close={handleClose}>
	<div class="tw-space-y-5 tw-font-sans">
		<!-- 참조 이미지: 넣으면 종류·화풍 단계를 대신하고 마지막 단계만 남는다 -->
		<div>
			<div class="tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-foreground">
				참조 이미지 <span class="tw-rounded-full tw-bg-muted tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-normal tw-text-muted-foreground">선택</span>
			</div>
			{#if hasRef}
				<div class="tw-relative tw-flex tw-min-h-[180px] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-border tw-bg-muted/40">
					<img class="tw-max-h-[220px] tw-w-auto tw-object-contain" src={src} alt="" />
					<button
						type="button"
						class="tw-absolute tw-right-2 tw-top-2 tw-inline-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-bg-black/45 tw-text-white hover:tw-bg-black/60"
						aria-label="참조 이미지 삭제"
						on:click={clickDeleteRefenceImage}
					>
						<svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
					</button>
				</div>
				<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
					참조 이미지가 종류·화풍을 대신해요. 분위기와 설명만 정하면 됩니다.
				</p>
			{:else}
				<!-- 참조는 두 군데서 온다: 로컬 파일, 그리고 **이미 라이브러리에 있는 이미지**.
				     예전엔 로컬 업로드만 돼서, 방금 AI로 만들어 둔 그림을 참조로 쓰려면
				     내려받았다 다시 올려야 했다. -->
				<div class="tw-grid tw-gap-2 sm:tw-grid-cols-2">
					<div
						class="tw-flex tw-min-h-[120px] tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-text-muted-foreground tw-transition-colors hover:tw-border-primary"
						on:click={() => addRefenceImageRef.click()}
						role="button"
						tabindex="0"
					>
						<svg class="tw-h-7 tw-w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14" /></svg>
						<span class="tw-text-sm tw-font-medium">내 파일 올리기</span>
					</div>
					<div
						class="tw-flex tw-min-h-[120px] tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-text-muted-foreground tw-transition-colors hover:tw-border-primary"
						on:click={() => (refPickerOpen = true)}
						role="button"
						tabindex="0"
					>
						<svg class="tw-h-7 tw-w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" /></svg>
						<span class="tw-text-sm tw-font-medium">이미지 에셋에서 고르기</span>
					</div>
				</div>
				<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
					넣으면 그 그림의 종류·화풍을 이어받아 만듭니다 (선택).
				</p>
			{/if}
			<input type="file" bind:this={addRefenceImageRef} on:change={onRefenceImageFileUpload} hidden />
		</div>

		<!-- 진행 표시 (다단계일 때만) -->
		{#if steps.length > 1}
			<div class="tw-flex tw-items-center tw-gap-2">
				{#each steps as _, i}
					<span class="tw-h-1.5 tw-flex-1 tw-rounded-full {i <= stepIndex ? 'tw-bg-primary' : 'tw-bg-muted'}"></span>
				{/each}
				<span class="tw-shrink-0 tw-text-xs tw-text-muted-foreground">{stepIndex + 1} / {steps.length}</span>
			</div>
		{/if}

		<!-- 현재 단계 -->
		{#if isFinal}
			<div>
				<div class="tw-mb-2 tw-text-sm tw-font-medium tw-text-foreground">분위기 <span class="tw-text-xs tw-font-normal tw-text-muted-foreground">선택</span></div>
				<div class="tw-flex tw-flex-wrap tw-gap-2">
					{#each MOODS as m}
						<button
							type="button"
							class="{chipBase} tw-px-4 tw-py-1.5 tw-text-sm {moodSel === m ? 'tw-border-primary tw-bg-primary/10 tw-font-medium tw-text-primary' : 'tw-border-border tw-text-foreground hover:tw-border-primary hover:tw-text-primary'}"
							on:click={() => pickMood(m)}
						>{m.label}</button>
					{/each}
				</div>
			</div>
			<div>
				<div class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">추가 설명 <span class="tw-text-xs tw-font-normal tw-text-muted-foreground">선택</span></div>
				<textarea
					class="tw-flex tw-min-h-[80px] tw-max-h-[200px] tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-text-foreground placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring"
					maxlength="500"
					bind:value={description}
					use:autoGrow={description}
					placeholder={"구체적으로 넣고 싶은 디테일이 있으면 적어주세요.\n예) 짧은 갈색 머리, 안경을 낀 20대"}
				/>
			</div>
		{:else}
			<div>
				<div class="tw-mb-2 tw-text-sm tw-font-medium tw-text-foreground">{current.step.title}</div>
				<div class="tw-flex tw-flex-wrap tw-gap-2">
					{#each current.step.options as opt}
						<button
							type="button"
							class="{chipBase} tw-px-5 tw-py-2.5 tw-text-sm {selections[current.step.key] === opt ? 'tw-border-primary tw-bg-primary/10 tw-font-medium tw-text-primary' : 'tw-border-border tw-text-foreground hover:tw-border-primary hover:tw-text-primary'}"
							on:click={() => pickChoice(current.step.key, opt)}
						>{opt.label}</button>
					{/each}
				</div>
			</div>
		{/if}

		<!-- 생성 결과 -->
		{#if hasResults}
			<div>
				<div class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">생성된 이미지</div>
				<div class="tw-grid tw-grid-cols-3 tw-gap-2">
					{#each aiImageList as aiImage}
						<button
							type="button"
							class="tw-aspect-square tw-cursor-pointer tw-overflow-hidden tw-rounded-lg tw-border-2 tw-transition-colors {aiImage.check ? 'tw-border-primary' : 'tw-border-transparent hover:tw-border-border'}"
							on:click={() => clickGeneratedImage(aiImage)}
						>
							<img class="tw-h-full tw-w-full tw-object-cover" src={aiImage.url} alt="" />
						</button>
					{/each}
				</div>
			</div>
		{/if}
	</div>

	<svelte:fragment slot="footer">
		{#if stepIndex > 0}
			<Button variant="outline" on:click={goBack}>← 이전</Button>
		{/if}
		{#if isFinal}
			<Button on:click={clickAIImageCreate}>{hasResults ? "다시 생성하기" : "생성하기"}</Button>
		{/if}
		{#if isAIImageChecked && hasResults}
			<Button on:click={handleSelectAIImage}>선택하기</Button>
		{/if}
	</svelte:fragment>
</Dialog>

<!-- 참조 이미지를 라이브러리에서 고르는 모달. 생성 팝업 위에 겹쳐 뜬다. -->
<ImagePicker
	bind:open={refPickerOpen}
	chatId={chat.chatId}
	contextFolder={chatAssetFolder(chat)}
	mode="single"
	on:select={onPickRefFromLibrary}
/>
